/**
 * InfluencerDetail.jsx — a single creator's profile.
 * Compact read-mostly view with an inline edit form for key fields.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { ChevronLeft, Save, Trash2, ExternalLink, Loader2 } from "lucide-react";
import { apiErrorMessage } from "@/lib/apiError";
import { fmtRelative } from "@/lib/format";

export default function InfluencerDetail() {
  const { iid } = useParams();
  const nav = useNavigate();
  const [inf, setInf] = useState(null);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [campaigns, setCampaigns] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/influencers/${iid}`);
      setInf(data); setEdit(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Not found")); nav("/app/influencers"); }
  }, [iid, nav]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const patch = {
        name: edit.name, primary_handle: edit.primary_handle,
        notes: edit.notes, status: edit.status,
        emails: edit.emails, phones: edit.phones,
        niches: edit.niches, tags: edit.tags,
        platforms: edit.platforms,
        rate_card: edit.rate_card,
        kyc: edit.kyc,
      };
      const { data } = await api.patch(`/influencers/${iid}`, patch);
      setInf(data); setEdit(data);
      toast.success("Saved");
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };

  const del = async () => {
    if (!window.confirm("Delete this creator?")) return;
    try {
      await api.delete(`/influencers/${iid}`);
      toast.success("Deleted");
      nav("/app/influencers");
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  if (!inf || !edit) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="inf-detail-page">
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={() => nav("/app/influencers")} className="text-muted-foreground hover:text-foreground text-xs inline-flex items-center gap-1">
          <ChevronLeft className="w-3.5 h-3.5" /> Directory
        </button>
        <h1 className="text-2xl font-bold flex-1 min-w-0 truncate">{inf.name}</h1>
        <Button size="sm" variant="outline" onClick={del} className="text-rose-600 hover:text-rose-700" data-testid="inf-del">
          <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
        </Button>
        <Button size="sm" onClick={save} disabled={saving} data-testid="inf-save">
          {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}Save
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2 space-y-4">
          {/* Basic */}
          <section className="border border-border p-4 space-y-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Basic</div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-[10px] uppercase">Name</Label><Input value={edit.name || ""} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="h-9 text-xs" /></div>
              <div><Label className="text-[10px] uppercase">Handle</Label><Input value={edit.primary_handle || ""} onChange={(e) => setEdit({ ...edit, primary_handle: e.target.value })} className="h-9 text-xs" /></div>
              <div><Label className="text-[10px] uppercase">Status</Label>
                <Select value={edit.status} onValueChange={(v) => setEdit({ ...edit, status: v })}>
                  <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["active", "prospect", "paused", "blacklisted"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-[10px] uppercase">Niches (comma-sep)</Label>
                <Input value={(edit.niches || []).join(", ")} onChange={(e) => setEdit({ ...edit, niches: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} className="h-9 text-xs" />
              </div>
              <div><Label className="text-[10px] uppercase">Emails (comma-sep)</Label>
                <Input value={(edit.emails || []).join(", ")} onChange={(e) => setEdit({ ...edit, emails: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} className="h-9 text-xs" />
              </div>
              <div><Label className="text-[10px] uppercase">Phones (comma-sep)</Label>
                <Input value={(edit.phones || []).join(", ")} onChange={(e) => setEdit({ ...edit, phones: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} className="h-9 text-xs" />
              </div>
            </div>
            <div><Label className="text-[10px] uppercase">Notes</Label>
              <textarea value={edit.notes || ""} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} rows={3} className="w-full text-xs px-2 py-1 bg-background border border-border resize-y" />
            </div>
          </section>

          {/* Platforms */}
          <section className="border border-border p-4 space-y-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Platforms</div>
            <div className="space-y-2">
              {(edit.platforms || []).map((p, i) => (
                <div key={i} className="grid grid-cols-[100px_1fr_100px_100px_28px] gap-2 items-center">
                  <Select value={p.platform} onValueChange={(v) => {
                    const arr = [...edit.platforms]; arr[i] = { ...p, platform: v }; setEdit({ ...edit, platforms: arr });
                  }}>
                    <SelectTrigger className="h-8 text-[11px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["instagram", "youtube", "tiktok", "linkedin", "twitter", "facebook", "snapchat", "pinterest", "twitch", "threads", "custom"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input value={p.handle} onChange={(e) => { const arr = [...edit.platforms]; arr[i] = { ...p, handle: e.target.value }; setEdit({ ...edit, platforms: arr }); }} className="h-8 text-[11px]" placeholder="handle" />
                  <Input type="number" value={p.followers} onChange={(e) => { const arr = [...edit.platforms]; arr[i] = { ...p, followers: Number(e.target.value) || 0 }; setEdit({ ...edit, platforms: arr }); }} className="h-8 text-[11px] text-right" placeholder="followers" />
                  <Input type="number" step="0.1" value={p.engagement_pct || ""} onChange={(e) => { const arr = [...edit.platforms]; arr[i] = { ...p, engagement_pct: Number(e.target.value) || null }; setEdit({ ...edit, platforms: arr }); }} className="h-8 text-[11px] text-right" placeholder="ER%" />
                  <button type="button" onClick={() => setEdit({ ...edit, platforms: edit.platforms.filter((_, j) => j !== i) })} className="text-muted-foreground hover:text-rose-500"><Trash2 className="w-3 h-3" /></button>
                </div>
              ))}
              <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setEdit({ ...edit, platforms: [...(edit.platforms || []), { platform: "instagram", handle: "", followers: 0 }] })}>+ Add platform</Button>
            </div>
          </section>

          {/* Rate card */}
          <section className="border border-border p-4 space-y-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Rate card (INR)</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {["per_post_inr", "per_reel_inr", "per_story_inr", "per_video_inr", "per_live_inr"].map((k) => (
                <div key={k}><Label className="text-[10px] uppercase">{k.replace(/_/g, " ").replace(" inr", "")}</Label>
                  <Input type="number" value={edit.rate_card?.[k] || ""} onChange={(e) => setEdit({ ...edit, rate_card: { ...(edit.rate_card || {}), [k]: Number(e.target.value) || null } })} className="h-9 text-xs" />
                </div>
              ))}
            </div>
            <div><Label className="text-[10px] uppercase">Package notes</Label>
              <Input value={edit.rate_card?.package_notes || ""} onChange={(e) => setEdit({ ...edit, rate_card: { ...(edit.rate_card || {}), package_notes: e.target.value } })} className="h-9 text-xs" />
            </div>
          </section>

          {/* KYC */}
          <section className="border border-border p-4 space-y-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">KYC & payment details</div>
            <div className="grid grid-cols-2 gap-2">
              {["pan_number", "gst_number", "bank_holder", "bank_account_no", "bank_ifsc", "upi_id"].map((k) => (
                <div key={k}><Label className="text-[10px] uppercase">{k.replace(/_/g, " ")}</Label>
                  <Input value={edit.kyc?.[k] || ""} onChange={(e) => setEdit({ ...edit, kyc: { ...(edit.kyc || {}), [k]: e.target.value } })} className="h-9 text-xs" />
                </div>
              ))}
            </div>
            <div><Label className="text-[10px] uppercase">Address</Label>
              <Input value={edit.kyc?.address || ""} onChange={(e) => setEdit({ ...edit, kyc: { ...(edit.kyc || {}), address: e.target.value } })} className="h-9 text-xs" />
            </div>
          </section>
        </div>

        <aside className="space-y-4">
          <div className="border border-border p-4">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Stats</div>
            <dl className="grid grid-cols-2 gap-3 mt-2 text-xs">
              <div><dt className="text-[10px] uppercase text-muted-foreground">Peak followers</dt><dd className="font-mono text-lg">{(inf.peak_followers || 0).toLocaleString("en-IN")}</dd></div>
              <div><dt className="text-[10px] uppercase text-muted-foreground">Tier</dt><dd className="font-mono text-lg">{inf.tier}</dd></div>
              <div><dt className="text-[10px] uppercase text-muted-foreground">Campaigns</dt><dd className="font-mono text-lg">{inf.campaigns_count || 0}</dd></div>
              <div><dt className="text-[10px] uppercase text-muted-foreground">Total earned</dt><dd className="font-mono text-lg">₹{(inf.total_earnings || 0).toLocaleString("en-IN")}</dd></div>
            </dl>
            <div className="text-[10px] text-muted-foreground mt-3">Added {fmtRelative(inf.created_at)}</div>
          </div>

          <div className="border border-border p-4">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Quick links</div>
            {(inf.platforms || []).filter((p) => p.url).map((p, i) => (
              <a key={i} href={p.url} target="_blank" rel="noreferrer" className="flex items-center justify-between text-xs py-1 hover:text-primary">
                <span>{p.platform}</span><ExternalLink className="w-3 h-3" />
              </a>
            ))}
            {(inf.platforms || []).every((p) => !p.url) && (
              <div className="text-[11px] text-muted-foreground">Add profile URLs in the Platforms section.</div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
